import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import '../styles/Mission.css';
import { FiSearch, FiEye, FiClock, FiTruck, FiUser } from 'react-icons/fi';
import { FaRegCalendarAlt } from 'react-icons/fa';
import { useMissions } from '../hooks/useMissions';
import { useSearch } from '../contexts/SearchContext';
import { useServiceCategories } from '../hooks/useServices';
import { useZones } from '../hooks/useZones';
import ResponsiveTable from '../components/ResponsiveTable';
import ViewToggle from '../components/ViewToggle';

const paymentColors = {
  'Payé': 'green',
  'Échec de paiement': 'red',
  'En cours de paiement': 'orange'
};

const statusFilters = ['Tous', 'En attente', 'En cours', 'Terminée', 'Annulée', 'Litige'];
const paymentFilters = ['Tous', 'Payé', 'En attente', 'Échec'];

const MissionPage = () => {
  const navigate = useNavigate();
  const { missions, loading, error } = useMissions();
  const { globalSearch } = useSearch();
  const { categories: serviceCategories } = useServiceCategories();
  const { zones } = useZones();

  // Générer dynamiquement les filtres de services
  const serviceFilters = ['Tous', ...(serviceCategories && serviceCategories.length > 0
    ? serviceCategories.map(cat => cat.name || cat)
    : [])];
  
  // Plus de données de démo - utiliser uniquement les données de l'API
  const [viewMode, setViewMode] = useState('cards');
  const [filters, setFilters] = useState({
    creation: '',
    execution: '',
    activeStatusFilter: 'Tous',
    activeServiceFilter: 'Tous',
    activePaymentFilter: 'Tous',
    evaluation: '',
    zone: '',
    search: ''
  });

  // Fonction de filtrage des missions
  const filteredMissions = missions.filter(mission => {
    // Filtrage par recherche globale (du header)
    if (globalSearch && !(
      mission.id.toString().includes(globalSearch.toLowerCase()) ||
      mission.client?.toLowerCase().includes(globalSearch.toLowerCase()) ||
      mission.prestataire?.toLowerCase().includes(globalSearch.toLowerCase()) ||
      mission.service_type?.toLowerCase().includes(globalSearch.toLowerCase())
    )) {
      return false;
    }

    // Filtrage par recherche locale (de la page)
    if (filters.search && !(
      mission.id.toString().includes(filters.search.toLowerCase()) ||
      mission.client?.toLowerCase().includes(filters.search.toLowerCase()) ||
      mission.prestataire?.toLowerCase().includes(filters.search.toLowerCase()) ||
      mission.service_type?.toLowerCase().includes(filters.search.toLowerCase())
    )) {
      return false;
    }

    // Filtrage par statut (mapper les filtres français vers les statuts API)
    if (filters.activeStatusFilter !== 'Tous') {
      const statusMapping = {
        'En attente': 'pending',
        'En cours': 'assigned',
        'Terminée': 'completed',
        'Annulée': 'cancelled',
        'Litige': 'disputed'
      };
      
      const expectedStatus = statusMapping[filters.activeStatusFilter];
      if (expectedStatus && mission.status !== expectedStatus) {
        return false;
      }
    }

    // Filtrage par type de service (simplifier car pas de service_types dans l'API)
    if (filters.activeServiceFilter !== 'Tous') {
      const serviceType = filters.activeServiceFilter.toLowerCase();
      if (!mission.service_name?.toLowerCase().includes(serviceType.toLowerCase())) {
        return false;
      }
    }

    // Filtrage par statut de paiement (adapter aux données API réelles)
    if (filters.activePaymentFilter !== 'Tous') {
      const paymentStatus = filters.activePaymentFilter.toLowerCase();
      if (paymentStatus === 'payé' && mission.payment_status !== 'completed') return false;
      if (paymentStatus === 'échec' && mission.payment_status !== 'failed') return false;
      if (paymentStatus === 'en attente' && mission.payment_status !== 'pending') return false;
    }

    // Filtrage par évaluation
    if (filters.evaluation && mission.rating?.toString() !== filters.evaluation) {
      return false;
    }

    // Filtrage par zone géographique (pas disponible dans l'API actuelle)
    // if (filters.zone && mission.zone?.toLowerCase() !== filters.zone.toLowerCase()) {
    //   return false;
    // }

    return true;
  });

  const handleMissionClick = (missionId) => {
    navigate(`/missions/${missionId}`);
  };

  const handleFilterChange = (filterType, value) => {
    setFilters(prev => ({
      ...prev,
      [filterType]: value
    }));
  };

  if (loading) {
    return (
      <div className="mission-container">
        <h2>Mission</h2>
        <div className="loading">Chargement des missions...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="mission-container">
        <h2>Mission</h2>
        <div className="error">Erreur lors du chargement des missions: {error}</div>
      </div>
    );
  }

  return (
    <div className="mission-container">
      <div className="missions-header-line">
        <h2 className="missions-title">Mission</h2>

        <div className="missions-filters">
          <div className="date-creation-container">
            <label htmlFor="date-creation">Création</label>
            <div className="date-input-wrapper">
              <FaRegCalendarAlt className="calendar-icon" />
              <input 
                type="date" 
                id="date-creation"
                value={filters.creation}
                onChange={(e) => handleFilterChange('creation', e.target.value)}
                className="date-input"
              />
            </div>
          </div>
          
          <div className="date-execution-container">
            <label htmlFor="date-execution">Exécution</label>
            <div className="date-input-wrapper">
              <FaRegCalendarAlt className="calendar-icon" />
              <input 
                type="date"
                id="date-execution" 
                value={filters.execution}
                onChange={(e) => handleFilterChange('execution', e.target.value)}
                className="date-input"
              />
            </div>
          </div>

          <div className="mission-status-toggle">
            {statusFilters.map((status, index) => (
              <button
                key={index}
                className={`status-btn ${filters.activeStatusFilter === status ? 'active' : ''}`}
                onClick={() => handleFilterChange('activeStatusFilter', status)}
              >
                {status}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="missions-second-line">
        <div className="missions-type-filters">
          {serviceFilters.map((tag, index) => (
            <button 
              key={index}
              className={`type-btn ${filters.activeServiceFilter === tag ? 'active' : ''}`}
              onClick={() => handleFilterChange('activeServiceFilter', tag)}
            >
              {tag}
            </button>
          ))}
        </div>
        
        <div className="missions-payment-filters">
          {paymentFilters.map((status, index) => (
            <button 
              key={index}
              className={`payment-btn ${filters.activePaymentFilter === status ? 'active' : ''}`}
              onClick={() => handleFilterChange('activePaymentFilter', status)}
            >
              {status}
            </button>
          ))}
        </div>

        <div className="missions-controls">
          <select 
            className="mission-select"
            value={filters.evaluation}
            onChange={(e) => handleFilterChange('evaluation', e.target.value)}
          >
            <option value="">Évaluation</option>
            <option value="5">5 étoiles</option>
            <option value="4">4 étoiles</option>
            <option value="3">3 étoiles</option>
            <option value="2">2 étoiles</option>
            <option value="1">1 étoile</option>
          </select>
          <select
            className="mission-select"
            value={filters.zone}
            onChange={(e) => handleFilterChange('zone', e.target.value)}
          >
            <option value="">Zone géographique</option>
            {zones && zones.length > 0 ? (
              zones.map((zone) => (
                <option key={zone.id} value={zone.name?.toLowerCase()}>
                  {zone.name}
                </option>
              ))
            ) : (
              <option value="" disabled>Chargement des zones...</option>
            )}
          </select>
        </div>
      </div>

      <div className="missions-search-section">
        <div className="search-wrapper">
          <FiSearch className="search-icon" />
          <input 
            className="search-bar"
            type="text" 
            placeholder="Recherche id, nom client, nom prestataire…" 
            value={filters.search}
            onChange={(e) => handleFilterChange('search', e.target.value)}
          />
        </div>
        <ViewToggle viewMode={viewMode} onToggle={setViewMode} />
        <button className="export-btn">Export CSV</button>
      </div>

      {viewMode === 'list' ? (
        <div className="missions-table-container">
          <table className="missions-table">
            <thead>
              <tr>
                <th>ID</th>
                <th>Client</th>
                <th>Prestataire</th>
                <th>Service</th>
                <th>Création</th>
                <th>Exécution</th>
                <th>Statut</th>
                <th>Paiement</th>
                <th>Note</th>
              </tr>
            </thead>
            <tbody>
              {filteredMissions.length === 0 ? (
                <tr><td colSpan="9" style={{ textAlign: 'center', padding: '40px', color: 'var(--text-muted)' }}>Aucune mission trouvée</td></tr>
              ) : (
                filteredMissions.map((mission, index) => {
                  const statusTranslations = {
                    'pending': 'En attente',
                    'assigned': 'En cours',
                    'completed': 'Terminée',
                    'cancelled': 'Annulée',
                    'disputed': 'Litige'
                  };
                  const statusLabel = statusTranslations[mission.status] || mission.status;
                  const statusClass = mission.status?.toLowerCase() === 'completed' ? 'terminee' : mission.status?.toLowerCase();
                  const paymentLabel = mission.payment_status === 'completed' ? 'Payé' :
                    mission.payment_status === 'failed' ? 'Échec' :
                    mission.payment_status === 'pending' ? 'En attente' : '—';
                  const paymentClass = mission.payment_status === 'completed' ? 'payer' :
                    mission.payment_status === 'failed' ? 'echec' :
                    mission.payment_status === 'pending' ? 'en-cours' : '';
                  const execDate = (() => {
                    if (!mission.executed_at) return 'À venir';
                    const d = new Date(mission.executed_at);
                    if (d.getTime() === new Date('1970-01-01').getTime()) return 'À venir';
                    return d.toLocaleDateString('fr-FR');
                  })();
                  return (
                    <tr key={mission.id || index} onClick={() => handleMissionClick(mission.id)} style={{ cursor: 'pointer' }}>
                      <td className="mission-id-cell">#{mission.id.substring(0, 8)}</td>
                      <td>{mission.client || '—'}</td>
                      <td>{mission.prestataire || '—'}</td>
                      <td><span className="tag transport">{mission.service_name || '—'}</span></td>
                      <td>{new Date(mission.created_at).toLocaleDateString('fr-FR')}</td>
                      <td>{execDate}</td>
                      <td><span className={`status-badge ${statusClass}`}>{statusLabel}</span></td>
                      <td>{paymentClass ? <span className={`payment-badge ${paymentClass}`}>{paymentLabel}</span> : '—'}</td>
                      <td className="table-stars">{'⭐'.repeat(mission.rating || 0)}</td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      ) : (
      <div className="mission-cards-figma">
        {filteredMissions.length === 0 ? (
          <div className="no-missions">
            <p>Aucune mission trouvée avec les filtres appliqués</p>
          </div>
        ) : (
          filteredMissions.map((mission, index) => (
            <div key={mission.id || index} className="mission-card-figma" onClick={() => handleMissionClick(mission.id)}>
              <div className="mission-header-figma">
                <div className="mission-id-status">
                  <span className="mission-id-figma"># Mission {mission.id.substring(0, 8)}</span>
                  <span className={`status-badge ${mission.status?.toLowerCase() === 'completed' ? 'terminee' : mission.status?.toLowerCase()}`}>
                    {(() => {
                      const statusTranslations = {
                        'pending': 'En attente',
                        'assigned': 'En cours',
                        'completed': 'Terminée',
                        'cancelled': 'Annulée',
                        'disputed': 'Litige'
                      };
                      return statusTranslations[mission.status] || mission.status;
                    })()}
                  </span>
                </div>
                <input type="checkbox" className="mission-checkbox" />
              </div>
              <div className="mission-client-figma">
                <div className="client-avatar-figma">
                  {mission.client?.charAt(0).toUpperCase() || 'A'}
                </div>
                <div className="client-info">
                  <strong>{mission.client || 'Client'}</strong>
                  {(mission.status === 'completed' || mission.status === 'disputed') && (
                    <div className={`payment-badge ${
                      mission.payment_status === 'completed' ? 'payer' : 
                      mission.payment_status === 'failed' ? 'echec' :
                      mission.payment_status === 'pending' ? 'en-cours' : 'payer'
                    }`}>
                      {mission.payment_status === 'completed' ? 'Payé' : 
                       mission.payment_status === 'failed' ? 'Échec de paiement' :
                       mission.payment_status === 'pending' ? 'En cours de paiement' : 'Payé'}
                    </div>
                  )}
                </div>
              </div>
              <div className="mission-tags-figma">
                <span className="tag transport">{mission.service_name || 'Transport'}</span>
              </div>
              <div className="mission-dates-stars-figma">
                <div className="mission-dates-figma">
                  <div className="date-item">
                    <img src="/icons/calendar.png" alt="date" className="mission-date-icon" />
                    <span>Création : {new Date(mission.created_at).toLocaleDateString('fr-FR')}</span>
                  </div>
                  <div className="date-item">
                    <img src="/icons/calendar.png" alt="date" className="mission-date-icon" />
                    <span>Exécution : {(() => {
                      if (!mission.executed_at || mission.executed_at === null) return 'à venir';
                      const executedDate = new Date(mission.executed_at);
                      if (executedDate.getTime() === new Date('1970-01-01').getTime()) return 'à venir';
                      return executedDate.toLocaleDateString('fr-FR');
                    })()}</span>
                  </div>
                </div>
                <div className="mission-stars-figma">
                  {[1, 2, 3, 4, 5].map(star => (
                    <span key={star} className={`star ${(mission.rating || 0) >= star ? 'filled' : ''}`}>⭐</span>
                  ))}
                </div>
              </div>
              <div className="mission-actions-figma">
                <button className="action-danger" onClick={(e) => e.stopPropagation()}>Annuler mission</button>
                <button className="action-secondary" onClick={(e) => e.stopPropagation()}>Rembourser</button>
                <button className="action-primary" onClick={(e) => {
                  e.stopPropagation();
                  navigate(`/missions/${mission.id}`);
                }}>Voir détail</button>
              </div>
            </div>
          ))
        )}
      </div>
      )}
    </div>
  );
};

export default MissionPage;
